import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import '../../styles/Gantt.css';

const DAY_IN_MS = 24 * 60 * 60 * 1000;

const TOOLTIP_WIDTH = 220;
const TOOLTIP_HEIGHT = 125;
const TOOLTIP_OFFSET = 14;

const dateFormatter = new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
});

const shortDateFormatter = new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short'
});

function toLocalDate(value) {
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split('-').map(Number);

        return new Date(year, month - 1, day);
    }

    const date = new Date(value);

    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function daysBetween(start, end) {
    return Math.round((end - start) / DAY_IN_MS);
}

/*
 * Transforma as linhas recebidas (uma por navio/dia) em:
 *  - days:  todos os dias do período, com ou sem navio (colunas);
 *  - ships: um item por navio, com a coluna inicial (offset) e
 *           quantas colunas ocupa (duration).
 */
function buildTimeline(data) {
    const shipsByName = new Map();
    let periodStart = null;
    let periodEnd = null;

    data.forEach(({ ship, date }) => {
        const day = toLocalDate(date);

        if (Number.isNaN(day.getTime())) {
            return;
        }

        if (!periodStart || day < periodStart) periodStart = day;
        if (!periodEnd || day > periodEnd) periodEnd = day;

        const name = ship?.trim();

        if (!name) {
            return;
        }

        const current = shipsByName.get(name);

        if (!current) {
            shipsByName.set(name, { name, start: day, end: day });
        } else {
            if (day < current.start) current.start = day;
            if (day > current.end) current.end = day;
        }
    });

    if (!periodStart) {
        return { ships: [], days: [] };
    }

    const days = Array.from(
        { length: daysBetween(periodStart, periodEnd) + 1 },
        (_, index) => new Date(
            periodStart.getFullYear(),
            periodStart.getMonth(),
            periodStart.getDate() + index
        )
    );

    const ships = [...shipsByName.values()]
        .sort((a, b) => a.start - b.start || a.name.localeCompare(b.name, 'pt-BR'))
        .map((ship) => ({
            ...ship,
            offset: daysBetween(periodStart, ship.start),
            duration: daysBetween(ship.start, ship.end) + 1
        }));

    return { ships, days };
}

function Gantt({ data = [], loading = false }) {
    const [tooltip, setTooltip] = useState(null);

    const { ships, days } = useMemo(() => buildTimeline(data), [data]);

    if (loading) {
        return <div className="gantt-chart-state">Carregando dados...</div>;
    }

    if (ships.length === 0) {
        return (
            <div className="gantt-chart-state">
                Nenhum navio disponível para o período selecionado.
            </div>
        );
    }

    /*
     * Posiciona o card ao lado do cursor, em coordenadas da janela
     * (o card é position: fixed), e vira para o outro lado quando
     * ele sairia pela direita/embaixo da tela.
     */
    const showTooltip = (ship) => (event) => {
        let left = event.clientX + TOOLTIP_OFFSET;
        let top = event.clientY + TOOLTIP_OFFSET;

        if (left + TOOLTIP_WIDTH > window.innerWidth) {
            left = event.clientX - TOOLTIP_WIDTH - TOOLTIP_OFFSET;
        }

        if (top + TOOLTIP_HEIGHT > window.innerHeight) {
            top = event.clientY - TOOLTIP_HEIGHT - TOOLTIP_OFFSET;
        }

        setTooltip({ ship, left, top });
    };

    return (
        <div className="gantt-chart-canvas">
            <div className="gantt-timeline" style={{ '--gantt-days': days.length }}>
                <div className="gantt-tracks">
                    {ships.map((ship, row) => (
                        <div
                            key={ship.name}
                            className="gantt-bar"
                            style={{
                                gridRow: row + 1,
                                gridColumn: `${ship.offset + 2} / span ${ship.duration}`
                            }}
                            onMouseMove={showTooltip(ship)}
                            onMouseLeave={() => setTooltip(null)}
                        >
                            {ship.name}
                        </div>
                    ))}
                </div>

                <div className="gantt-days">
                    {days.map((day, index) => (
                        <span
                            key={day.getTime()}
                            className="gantt-day"
                            style={{ gridColumn: index + 2 }}
                        >
                            {shortDateFormatter.format(day).replace('.', '')}
                        </span>
                    ))}
                </div>
            </div>

            {/*
              * O card é renderizado direto no <body> (portal): assim ele
              * nunca é cortado pelo overflow do card/modal em volta, nem
              * aumenta a altura do gráfico.
              */}
            {tooltip && createPortal(
                <div
                    className="gantt-react-tooltip"
                    style={{ left: tooltip.left, top: tooltip.top }}
                >
                    <div className="gantt-react-tooltip-title">🚢 {tooltip.ship.name}</div>
                    <div className="gantt-react-tooltip-divider" />
                    <div className="gantt-react-tooltip-row">
                        <span>Início</span>
                        <strong>{dateFormatter.format(tooltip.ship.start)}</strong>
                    </div>
                    <div className="gantt-react-tooltip-row">
                        <span>Término</span>
                        <strong>{dateFormatter.format(tooltip.ship.end)}</strong>
                    </div>
                    <div className="gantt-react-tooltip-row">
                        <span>Duração</span>
                        <strong>
                            {tooltip.ship.duration} {tooltip.ship.duration === 1 ? 'dia' : 'dias'}
                        </strong>
                    </div>
                </div>,
                document.body
            )}

            <div className="gantt-chart-summary">
                {ships.map(({ name, start, end }) => (
                    <span key={name}>
                        {name}: {dateFormatter.format(start)} a {dateFormatter.format(end)}
                    </span>
                ))}
            </div>
        </div>
    );
}

export default Gantt;